import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import useMediaLibrary from '../../hooks/useMediaLibrary';
import MediaThumbnailGrid from './MediaThumbnailGrid';

export default function MediaLibraryPickerModal({ show, onClose, onSelect, selectedId }) {
  const fileInputRef = useRef(null);
  const [search, setSearch] = useState('');
  const [uploadNotice, setUploadNotice] = useState('');
  const {
    readyItems,
    loading,
    error,
    uploading,
    uploadFile,
    fetchDownloadUrl,
  } = useMediaLibrary({ enabled: show });

  const filteredItems = readyItems.filter((item) => {
    if (!search.trim()) return true;
    return item.filename.toLowerCase().includes(search.trim().toLowerCase());
  });

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadNotice('');
    try {
      const result = await uploadFile(file);
      if (result?.media) {
        if (result.deduplicated) {
          setUploadNotice('Added existing image from your library (duplicate detected).');
        }
        onSelect(result.media);
        onClose();
      }
    } catch {
      // error surfaced via hook
    }
  };

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title="Choose from media library"
      size="lg"
      footer={
        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
          Cancel
        </button>
      }
    >
      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" />
              Uploading…
            </>
          ) : (
            <>
              <i className="bi bi-upload me-1" />
              Upload image
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="d-none"
          onChange={handleFileChange}
        />
        <input
          type="search"
          className="form-control form-control-sm ms-auto"
          style={{ maxWidth: 240 }}
          placeholder="Search by filename…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {uploadNotice && (
        <div className="alert alert-info py-2 small mb-3" role="status">
          {uploadNotice}
        </div>
      )}

      <MediaThumbnailGrid
        items={filteredItems}
        loading={loading}
        error={error}
        fetchDownloadUrl={fetchDownloadUrl}
        selectedId={selectedId}
        onSelect={(item) => {
          onSelect(item);
          onClose();
        }}
        emptyMessage="No images in your library yet. Upload one to get started."
      />
    </AdminModal>
  );
}

MediaLibraryPickerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
};

MediaLibraryPickerModal.defaultProps = {
  selectedId: null,
};
